import { AnimatePresence, motion } from "framer-motion";
import { Upload, Download } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "./ui/Button";
import { Label } from "./ui/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/Select";
import { toast } from "sonner";
import { fetchFields } from "../services/dynamicFieldService.js";

export default function BulkUploadModal({
  isOpen,
  closeModal,
  bulkAssignedTo,
  setBulkAssignedTo,
  bulkFile,
  bulkRowCount,
  handleBulkFileChange,
  handleBulkContinue,
  triggerFilePicker,
  reps = [],
  currentUser,
  isFileInvalid,
  setIsFileInvalid,
}) {
  const fileInputRef = useRef(null);
  const [dynamicFields, setDynamicFields] = useState([]);

  useEffect(() => {
    if (isOpen) {
      (async () => {
        try {
          const res = await fetchFields();
          setDynamicFields(res.data || []);
        } catch {
          setDynamicFields([]);
        }
      })();
    }
  }, [isOpen]);

  const handleFilePickerClick = () => {
    setIsFileInvalid(false);
    fileInputRef.current?.click();
  };

  const handleDownloadTemplate = () => {
    // Fixed headers
    const fixedHeaders = ["leadname", "phone", "email"];
    // Dynamic field headers (snake_case from field labels)
    const dynamicHeaders = dynamicFields.map((f) =>
      f.field_label.toLowerCase().replace(/\s+/g, "_")
    );
    // Remaining fixed headers
    const trailingHeaders = ["address", "comment"];

    const allHeaders = [...fixedHeaders, ...dynamicHeaders, ...trailingHeaders];

    // Build sample row with option hints
    const sampleRow = allHeaders.map((header) => {
      const field = dynamicFields.find(
        (f) => f.field_label.toLowerCase().replace(/\s+/g, "_") === header
      );
      if (field && field.options?.length > 0) {
        // Show first 3 options as hint
        const hints = field.options
          .slice(0, 3)
          .map((o) => o.option_label)
          .join(" | ");
        return hints;
      }
      return "";
    });

    const csvContent =
      allHeaders.join(",") + "\n" + sampleRow.join(",") + "\n";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk-lead-template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOS) {
      toast("Download complete — check Safari or Files app.");
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex flex-col justify-end"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Overlay */}
          <motion.div
            className="absolute inset-0 bg-black/40 backdrop-blur-xs"
            onClick={closeModal}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal (Bottom Sheet Style) */}
          <motion.div
            key="bulk-upload-modal"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{
              type: "spring",
              stiffness: 180,
              damping: 22,
              mass: 0.5,
            }}
            className="relative w-full bg-white rounded-t-3xl shadow-lg p-5 z-50 overflow-y-auto max-h-[80vh]"
          >
            {/* Handle Bar */}
            <div
              className="bg-gray-400/70 absolute top-1 left-1/2 -translate-x-1/2 h-1 w-16 rounded-full cursor-pointer"
              onClick={closeModal}
            ></div>

            {/* Title */}
            <h2 className="text-base font-semibold text-gray-800 mb-4 text-center">
              Add Multiple Leads
            </h2>

            <div className="space-y-4">
              {/* Step 1: Download Template */}
              <div>
                <Label className="text-sm font-medium text-slate-800">
                  Step 1: Download Template
                </Label>
                <Button
                  variant="outline"
                  type="button"
                  className="w-full gap-2 h-8 text-xs text-gray-700 mt-1 border-blue-100/50"
                  onClick={handleDownloadTemplate}
                >
                  <Download className="w-4 h-4" />
                  Download CSV Template
                </Button>
                {dynamicFields.length > 0 && (
                  <p className="text-[10px] text-gray-400 mt-1">
                    Template includes: leadname, phone, email,{" "}
                    {dynamicFields
                      .map((f) =>
                        f.field_label.toLowerCase().replace(/\s+/g, "_")
                      )
                      .join(", ")}
                    , address, comment
                  </p>
                )}
              </div>

              {/* Step 2: Assign To */}
              <div>
                <Label className="text-sm font-medium text-slate-800">
                  Step 2: Assign To
                </Label>
                <Select
                  value={bulkAssignedTo || "self"}
                  onValueChange={(val) => setBulkAssignedTo(val)}
                >
                  <SelectTrigger className="w-full h-8 text-xs border-blue-100/50 mt-1">
                    <SelectValue placeholder="Select assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">Self</SelectItem>
                    {Array.isArray(reps) &&
                      reps
                        .filter((rep) => rep.id !== currentUser?.id)
                        .map((rep) => (
                          <SelectItem
                            key={rep.id}
                            value={rep.id}
                            className="text-xs"
                          >
                            {rep.name}
                          </SelectItem>
                        ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Step 3: Upload CSV */}
              <div>
                <Label className="text-sm font-medium text-slate-800">
                  Step 3: Upload CSV File
                </Label>
                <input
                  id="bulkCsvInput"
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleBulkFileChange}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  className="w-full h-8 gap-2 text-xs border-blue-100/50 mt-1"
                  onClick={handleFilePickerClick}
                >
                  <Upload className="w-4 h-4" />
                  Choose CSV File
                </Button>

                {bulkFile && !isFileInvalid && (
                  <div className="space-y-1 p-3 bg-slate-50 rounded-lg mt-2">
                    <p className="text-xs font-medium text-slate-600">
                      File Selected
                    </p>
                    <p className="text-sm text-slate-900">{bulkFile.name}</p>
                    <p className="text-xs text-slate-600">
                      Estimated rows: {bulkRowCount}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="flex justify-end gap-2 mt-5">
              <Button
                variant="ghost"
                onClick={closeModal}
                className="h-8 px-3 text-xs rounded-full border border-gray-200 text-gray-600"
              >
                Cancel
              </Button>
              <Button
                className="h-8 px-4 text-xs rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391] text-white border"
                disabled={!bulkFile || !bulkAssignedTo || isFileInvalid}
                onClick={handleBulkContinue}
              >
                Upload
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
